// Normalized image coordinates -> local Web Mercator metres. Translation/scale
// normalization keeps the four-point solve stable at real-world coordinates.
const R=6378137;
export function mercator(lat,lon){return [R*lon*Math.PI/180,R*Math.log(Math.tan(Math.PI/4+lat*Math.PI/360))];}
export function geographic(x,y){return [Math.atan(Math.sinh(y/R))*180/Math.PI,x/R*180/Math.PI];}
function solve(a,b){
 const m=a.map((row,i)=>[...row,b[i]]);
 for(let c=0;c<8;c++){
  let pivot=c;for(let r=c+1;r<8;r++)if(Math.abs(m[r][c])>Math.abs(m[pivot][c]))pivot=r;
  if(Math.abs(m[pivot][c])<1e-9)throw new Error('Control points are too close or collinear. Choose four well-spaced corners.');
  [m[c],m[pivot]]=[m[pivot],m[c]];const d=m[c][c];m[c]=m[c].map(v=>v/d);
  for(let r=0;r<8;r++)if(r!==c){const f=m[r][c];m[r]=m[r].map((v,i)=>v-f*m[c][i]);}
 }
 return m.map(row=>row[8]);
}
export function hull(points){
 const list=[...points].sort((a,b)=>a[0]-b[0]||a[1]-b[1]);
 const cross=(o,a,b)=>(a[0]-o[0])*(b[1]-o[1])-(a[1]-o[1])*(b[0]-o[0]);
 const side=arr=>{const out=[];for(const p of arr){while(out.length>1&&cross(out[out.length-2],out[out.length-1],p)<=0)out.pop();out.push(p);}return out;};
 return [...side(list).slice(0,-1),...side(list.reverse()).slice(0,-1)];
}
export function insideHull(point,polygon){
 let sign=0;for(let i=0;i<polygon.length;i++){const a=polygon[i],b=polygon[(i+1)%polygon.length];const v=(b[0]-a[0])*(point[1]-a[1])-(b[1]-a[1])*(point[0]-a[0]);if(Math.abs(v)<1e-9)continue;if(sign&&Math.sign(v)!==sign)return false;sign=Math.sign(v);}return polygon.length>=3;
}
export function fitRegistration(points){
 if(points.length!==4||points.some(p=>![p.x,p.y,p.lat,p.lon].every(Number.isFinite)||p.x<0||p.x>1||p.y<0||p.y>1||Math.abs(p.lat)>85||Math.abs(p.lon)>180))throw new Error('Choose four valid image/map point pairs.');
 const polygon=hull(points.map(p=>[p.x,p.y]));if(polygon.length!==4)throw new Error('Choose four corners surrounding one planar panel area.');
 const world=points.map(p=>mercator(p.lat,p.lon)),origin=world[0];
 if(hull(world).length!==4)throw new Error('Map points must surround a non-degenerate area.');
 const scale=Math.max(...world.flatMap(p=>[Math.abs(p[0]-origin[0]),Math.abs(p[1]-origin[1])]));
 if(scale<0.1)throw new Error('Map points must cover a larger area.');
 const a=[],b=[];
 points.forEach((p,i)=>{const u=(world[i][0]-origin[0])/scale,v=(world[i][1]-origin[1])/scale;
 a.push([p.x,p.y,1,0,0,0,-u*p.x,-u*p.y],[0,0,0,p.x,p.y,1,-v*p.x,-v*p.y]);b.push(u,v);});
 const h=solve(a,b);
 const denominators=polygon.map(([x,y])=>h[6]*x+h[7]*y+1);
 if(denominators.some(d=>Math.abs(d)<1e-8||Math.sign(d)!==Math.sign(denominators[0])))throw new Error('Point order produces an invalid perspective. Check the matching corners.');
 const project=(x,y)=>{const d=h[6]*x+h[7]*y+1;if(Math.abs(d)<1e-8)return null;
 const xy=[origin[0]+scale*(h[0]*x+h[1]*y+h[2])/d,origin[1]+scale*(h[3]*x+h[4]*y+h[5])/d];const ll=geographic(...xy);return ll.every(Number.isFinite)&&Math.abs(ll[0])<=85&&Math.abs(ll[1])<=180?ll:null;};
 return {project,polygon,matrix:[...h,1],origin,scale,version:'homography-mercator-v1'};
}
export function candidatePosition(candidate,fit){
 const x=candidate.peak?.xNorm??((candidate.box?.x1+candidate.box?.x2)/2),y=candidate.peak?.yNorm??((candidate.box?.y1+candidate.box?.y2)/2);
 if(!Number.isFinite(x)||!Number.isFinite(y)||!insideHull([x,y],fit.polygon))return null;
 return fit.project(x,y);
}
